import { Box } from '@mui/material';
import { Divider } from '@portals/core/src/components/Divider/Divider';
import { Typography } from '@portals/core/src/components/Typography/Typography';
import { theme } from '@portals/core/src/themes/immobilien/main';
import { EstateDetail } from '@portals/sip-client-data/src/general/ApiClientTypes';
import { Form, Formik } from 'formik';
import { getFixedT } from 'i18next';
import React from 'react';

import { usePreventSafariAutozoom } from '../../../../../../hooks';
import { getStyles } from '../EstateContact.styles';
import { BrokerInfoSection } from './BrokerInfoSection/BrokerInfoSection';
import { ContactAddressSection } from './ContactAddressSection/ContactAddressSection';
import { ContactDataSection } from './ContactDataSection/ContactDataSection';
import { createEstateContactFormSchema } from './EstateContactForm.schema';
import { EstateTeaserSection } from './EstateTeaserSection/EstateTeaserSection';
import { FormFooterSection } from './FormFooterSection/FormFooterSection';
import { MessageSection } from './MessageSection/MessageSection';

interface EstateContactFormProps {
  estate: EstateDetail;
  showHeadline: boolean;
  showPrivacyPolicy: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  showConsumerInformation: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  onSubmit?: (values: unknown) => Promise<void>;
}

const t = getFixedT(null, 'core-immobilien');

export const EstateContactForm = ({
  estate,
  showHeadline,
  showPrivacyPolicy,
  showConsumerInformation,
  onSubmit,
}: EstateContactFormProps): React.ReactElement => {
  const styles = getStyles(theme);

  const { isUserEstate, provider, broker, contact } = estate;

  const instituteName = provider?.company;
  const phoneRequired = contact?.contactPhoneRequired ?? false;
  const addressRequired = contact?.contactAddressRequired ?? false;
  const consumerInformation = contact?.consumerInformation;

  const preventSafariAutozoom = usePreventSafariAutozoom();

  return (
    <Formik
      initialValues={{
        subject: `${t('detailPage.objectNumber', {
          id: estate.id,
          externId: estate.externId,
          interpolation: { escapeValue: false },
        })} | ${estate.title}`,
        message: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        street: '',
        streetNumber: '',
        zip: '',
        city: '',
        accept: false,
      }}
      validateOnMount
      validateOnBlur={true}
      validationSchema={createEstateContactFormSchema(t, phoneRequired, addressRequired, isUserEstate)}
      onSubmit={onSubmit}
    >
      {(formik) => {
        return (
          <Box sx={styles.outerContainer}>
            {showHeadline && <Typography variant="h3">{t('detailPage.contractProvider')}</Typography>}
            <Box sx={styles.headContainer}>
              <BrokerInfoSection broker={broker} instituteName={instituteName} />
              <Divider />

              <EstateTeaserSection estate={estate} />
            </Box>
            <Form>
              <Box sx={styles.formContainer}>
                <Box sx={styles.formSection}>
                  <MessageSection preventSafariAutozoom={preventSafariAutozoom} />
                </Box>
                <Box sx={styles.formSection}>
                  <ContactDataSection phoneRequired={phoneRequired} preventSafariAutozoom={preventSafariAutozoom} />
                </Box>
                {!isUserEstate && (
                  <Box sx={styles.formSection}>
                    <ContactAddressSection
                      addressRequired={addressRequired}
                      preventSafariAutozoom={preventSafariAutozoom}
                    />
                  </Box>
                )}
              </Box>

              <FormFooterSection
                isUserEstate={isUserEstate}
                showPrivacyPolicy={showPrivacyPolicy}
                showConsumerInformation={showConsumerInformation}
                consumerInformation={consumerInformation}
                isSubmitting={formik.isSubmitting}
                isValid={formik.isValid}
              />
            </Form>
          </Box>
        );
      }}
    </Formik>
  );
};

EstateContactForm.displayName = 'EstateContactForm';
